// packages/web/src/lib/catalog/detector.ts
// Core catalog detection algorithm

import { getCachedCatalog, isCatalogCacheValid, setCachedCatalog } from './cache';
import type { CatalogInstance } from './types';

// =============================================================================
// DOMAIN DETECTION HELPERS
// =============================================================================
// Supports both dev (visionarynow.com) and prod (aithon.tech with blue/green)

function isMainDomain(hostname: string): boolean {
  const h = hostname.toLowerCase();

  // Dev domains
  if (h === 'visionarynow.com' || h === 'www.visionarynow.com') {
    return true;
  }

  // Prod main domains (aithon.tech with blue/green)
  if (h === 'aithon.tech' || h === 'www.aithon.tech') {
    return true;
  }

  // Prod blue/green subdomains (if Apache doesn't strip them)
  // Pattern: blue.aithon.tech, green.aithon.tech, or any subdomain ending in .aithon.tech
  if (h.endsWith('.aithon.tech')) {
    return true;
  }

  return false;
}

function getDefaultCatalogBranding(domain: string) {
  // Prod domain = Aithon branding
  if (domain.includes('aithon')) {
    return {
      logo: '/logo-aithon.png',
      primaryColor: '#1a73e8',
      secondaryColor: '#34a853',
      name: 'Aithon Marketplace',
    };
  }

  // Default dev branding
  return {
    logo: '/logo-techadds.png',
    primaryColor: '#D4AF37',
    secondaryColor: '#dc004e',
    name: 'TechAdds Marketplace',
  };
}

// =============================================================================

/**
 * Detect catalog instance from current URL/domain or query parameters
 */
export async function detectCatalogInstance(): Promise<CatalogInstance | null> {
  if (typeof window === 'undefined') {
    return null; // SSR safe
  }

  const hostname = window.location.hostname.toLowerCase();
  const pathname = window.location.pathname.toLowerCase();
  const urlParams = new URLSearchParams(window.location.search);
  const catalogParam = urlParams.get('catalog');

  console.log('[CATALOG-DETECTOR] Detecting catalog from hostname:', hostname);
  console.log('[CATALOG-DETECTOR] Pathname:', pathname);
  console.log('[CATALOG-DETECTOR] Query parameter catalog:', catalogParam);

  // Check cache first
  const cacheKey = catalogParam
    ? `${hostname}${pathname}?catalog=${catalogParam}`
    : `${hostname}${pathname}`;
  if (isCatalogCacheValid(cacheKey)) {
    console.log('[CATALOG-DETECTOR] Using cached catalog instance');
    return getCachedCatalog(cacheKey)!;
  }

  try {
    // Check authentication status
    const { hasAuthTokens } = await import('../auth/session-manager');
    const isAuthenticated = hasAuthTokens();

    console.log('[CATALOG-DETECTOR] Authentication status:', isAuthenticated);

    // Phase 6: Check for /c/catalogname URL pattern first
    const catalogPathMatch = pathname.match(/^\/c\/([a-z0-9-]+)(\/.*)?$/);
    if (catalogPathMatch) {
      const catalogName = catalogPathMatch[1];
      console.log('[CATALOG-DETECTOR] Phase 6: Detected /c/catalogname pattern:', catalogName);

      const catalogInfo = await fetchCatalogFromCatalogName(catalogName, isAuthenticated);
      if (catalogInfo) {
        setCachedCatalog(cacheKey, catalogInfo);
        console.log(
          '[CATALOG-DETECTOR] Phase 6: Found catalog from /c/catalogname:',
          catalogInfo.name,
        );
        return catalogInfo;
      }
    }

    // Phase 5A: Check for query parameter
    if (catalogParam) {
      console.log('[CATALOG-DETECTOR] Phase 5A: Using query parameter catalog:', catalogParam);
      const catalogInfo = await fetchCatalogFromQueryParam(catalogParam, isAuthenticated);

      if (catalogInfo) {
        setCachedCatalog(cacheKey, catalogInfo);
        console.log(
          '[CATALOG-DETECTOR] Phase 5A: Found catalog from query param:',
          catalogInfo.name,
        );
        return catalogInfo;
      }
    }

    // Check if this is a main domain (dev or prod) - use main catalog by default
    if (isMainDomain(hostname)) {
      console.log(
        '[CATALOG-DETECTOR] Main domain detected (' + hostname + '), using default main catalog',
      );
      const branding = getDefaultCatalogBranding(hostname);
      const defaultCatalog = {
        id: null,
        tenantId: null,
        subdomain: null,
        isMainCatalog: true,
        name: branding.name,
        description: 'Main marketplace catalog',
        status: 'active',
        assortmentRules: { mode: 'include', filters: {} },
        branding: branding,
      };
      setCachedCatalog(cacheKey, defaultCatalog);
      return defaultCatalog;
    }

    // Fallback to subdomain detection
    const subdomainMatch = hostname.match(/^([a-z0-9-]+)\.techadds\.net$/);
    const isSubdomain = !!subdomainMatch;
    const subdomain = subdomainMatch ? subdomainMatch[1] : null;

    console.log('[CATALOG-DETECTOR] Checking for subdomain:', subdomain);

    // Query API to get catalog information (with auth awareness)
    const catalogInfo = await fetchCatalogFromAPI(
      isSubdomain,
      subdomain,
      hostname,
      isAuthenticated,
    );

    if (catalogInfo) {
      // Cache the result
      setCachedCatalog(cacheKey, catalogInfo);

      console.log('[CATALOG-DETECTOR] Found catalog:', catalogInfo.name);
      return catalogInfo;
    }

    // FAANG-GRADE: If no catalog found, return a default catalog for both authenticated and anonymous users
    // This prevents the marketplace from failing due to catalog detection issues
    console.log(
      '[CATALOG-DETECTOR] No catalog found, returning default catalog for',
      isAuthenticated ? 'authenticated' : 'anonymous',
      'user',
    );
    const fallbackBranding = getDefaultCatalogBranding(hostname);
    const defaultCatalog = {
      id: 'default',
      tenantId: null,
      subdomain: null,
      isMainCatalog: true,
      name: fallbackBranding.name,
      description: 'Default marketplace catalog',
      status: 'active',
      assortmentRules: { mode: 'include', filters: {} },
      branding: fallbackBranding,
    };
    setCachedCatalog(cacheKey, defaultCatalog);
    return defaultCatalog;
  } catch (error) {
    console.error('[CATALOG-DETECTOR] Error detecting catalog:', error);

    // FAANG-GRADE: Return fallback catalog on error to prevent auth redirects
    console.log('[CATALOG-DETECTOR] Returning fallback catalog due to error');
    const fallbackCatalog = {
      id: 'fallback',
      tenantId: null,
      subdomain: null,
      isMainCatalog: true,
      name: 'TechAdds Marketplace',
      description: 'Fallback marketplace catalog',
      status: 'active',
      assortmentRules: { mode: 'include', filters: {} },
      branding: {
        logo: '/logo192.png',
        primaryColor: '#D4AF37',
        secondaryColor: '#dc004e',
        name: 'TechAdds Marketplace',
      },
    };
    setCachedCatalog(cacheKey, fallbackCatalog);
    return fallbackCatalog;
  }
}

/**
 * Fetch catalog information from API with timeout and retry logic
 */
async function fetchCatalogFromAPI(
  isSubdomain: boolean,
  subdomain: string | null,
  hostname: string,
  isAuthenticated: boolean = false,
): Promise<CatalogInstance | null> {
  // First check localStorage for mock catalog instances
  if (typeof window !== 'undefined') {
    try {
      const mockInstances = localStorage.getItem('mock_catalog_instances');
      if (mockInstances) {
        const instances = JSON.parse(mockInstances);
        console.log('[CATALOG-DETECTOR] Checking mock catalog instances:', instances.length);

        // Find matching catalog instance
        let matchingInstance = null;

        if (isSubdomain && subdomain) {
          // Look for catalog with matching subdomain
          matchingInstance = instances.find((inst: any) => inst.subdomain === subdomain);
        } else if (!isSubdomain) {
          // Look for main catalog (no subdomain)
          matchingInstance = instances.find((inst: any) => !inst.subdomain);
        }

        if (matchingInstance) {
          console.log('[CATALOG-DETECTOR] Found mock catalog instance:', matchingInstance.name);
          return {
            id: matchingInstance.id,
            tenantId: matchingInstance.tenant_id,
            subdomain: matchingInstance.subdomain,
            isMainCatalog: !matchingInstance.subdomain,
            name: matchingInstance.name,
            description: matchingInstance.description,
            status: matchingInstance.status,
            assortmentRules: {
              mode: matchingInstance.assortmentMode,
              ...matchingInstance.filters,
            },
            branding: matchingInstance.branding,
          };
        }
      }
    } catch (error) {
      console.warn('[CATALOG-DETECTOR] Error checking mock catalog instances:', error);
    }
  }

  const maxRetries = 3;
  const timeoutMs = 5000; // 5 second timeout

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Use the new public catalog detection endpoint
      const params = new URLSearchParams();
      if (subdomain) {
        params.append('subdomain', subdomain);
      }
      if (hostname) {
        params.append('hostname', hostname);
      }

      console.log(
        `[CATALOG-DETECTOR] Querying catalog detection API (attempt ${attempt}/${maxRetries}):`,
        { subdomain, hostname },
      );

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(`/api/catalog/detect?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.catalogInstance) {
          console.log('[CATALOG-DETECTOR] Found catalog instance:', data.catalogInstance.name);
          if (data.meta) {
            console.log('[CATALOG-DETECTOR] Resolution details:', data.meta);
          }
          return {
            id: data.catalogInstance.id,
            tenantId: data.catalogInstance.tenantId,
            subdomain: data.catalogInstance.subdomain,
            isMainCatalog: data.catalogInstance.isMainCatalog,
            name: data.catalogInstance.name,
            description: data.catalogInstance.description,
            status: data.catalogInstance.status,
            assortmentRules: data.catalogInstance.assortmentRules,
            branding: data.catalogInstance.branding,
          };
        } else {
          console.warn(
            '[CATALOG-DETECTOR] API returned success but no catalogInstance in response',
          );
        }
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.warn(
          `[CATALOG-DETECTOR] Catalog detection API returned error ${response.status}:`,
          errorText,
        );

        // Don't retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          break;
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.warn(
          `[CATALOG-DETECTOR] API call timed out after ${timeoutMs}ms (attempt ${attempt}/${maxRetries})`,
        );
      } else {
        console.error(
          `[CATALOG-DETECTOR] API call failed (attempt ${attempt}/${maxRetries}):`,
          error,
        );
      }

      // If this was the last attempt, don't retry
      if (attempt === maxRetries) {
        break;
      }

      // Wait before retrying (exponential backoff)
      const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      console.log(`[CATALOG-DETECTOR] Waiting ${delayMs}ms before retry...`);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  console.error('[CATALOG-DETECTOR] All attempts to fetch catalog failed');
  return null;
}

/**
 * Fetch catalog information from /c/catalogname URL pattern (Phase 6)
 */
async function fetchCatalogFromCatalogName(
  catalogName: string,
  isAuthenticated: boolean = false,
): Promise<CatalogInstance | null> {
  const maxRetries = 3;
  const timeoutMs = 5000; // 5 second timeout

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(
        `[CATALOG-DETECTOR] Phase 6: Querying catalog by name (attempt ${attempt}/${maxRetries}):`,
        catalogName,
      );

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      // Query the new /c/catalogname endpoint to get catalog info
      const response = await fetch(`/api/marketplace/c/${catalogName}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        const catalog = data?.catalog || data;
        console.log('[CATALOG-DETECTOR] Phase 6: Found catalog by name:', catalog?.name);

        if (!catalog?.id && !catalog?.name) {
          console.warn('[CATALOG-DETECTOR] Phase 6: Unexpected catalog-by-name response shape:', data);
          return null;
        }

        // Transform the response to match CatalogInstance interface
        return {
          id: catalog.id,
          tenantId: catalog.tenant_id,
          subdomain: catalog.subdomain,
          catalogName: catalog.catalog_name,
          isMainCatalog: catalog.is_main_catalog,
          name: catalog.name,
          description: catalog.description,
          status: catalog.status,
          assortmentRules: catalog.assortment_rules,
          branding: catalog.branding,
        };
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.warn(
          `[CATALOG-DETECTOR] Phase 6: Catalog by name API returned error ${response.status}:`,
          errorText,
        );

        // Don't retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          break;
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.warn(
          `[CATALOG-DETECTOR] Phase 6: API call timed out after ${timeoutMs}ms (attempt ${attempt}/${maxRetries})`,
        );
      } else {
        console.error(
          `[CATALOG-DETECTOR] Phase 6: API call failed (attempt ${attempt}/${maxRetries}):`,
          error,
        );
      }

      // If this was the last attempt, don't retry
      if (attempt === maxRetries) {
        break;
      }

      // Wait before retrying (exponential backoff)
      const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      console.log(`[CATALOG-DETECTOR] Phase 6: Waiting ${delayMs}ms before retry...`);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  console.error('[CATALOG-DETECTOR] Phase 6: All attempts to fetch catalog by name failed');
  return null;
}

/**
 * Fetch catalog information from query parameter (Phase 5A testing)
 */
async function fetchCatalogFromQueryParam(
  catalogSlug: string,
  isAuthenticated: boolean = false,
): Promise<CatalogInstance | null> {
  const maxRetries = 3;
  const timeoutMs = 5000; // 5 second timeout

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(
        `[CATALOG-DETECTOR] Phase 5A: Querying catalog by slug (attempt ${attempt}/${maxRetries}):`,
        catalogSlug,
      );

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      // Use the partners API to get catalog info by subdomain/slug
      const response = await fetch(`/api/partners/${catalogSlug}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        console.log('[CATALOG-DETECTOR] Phase 5A: Found catalog by slug:', data.companyName);

        // Transform the response to match CatalogInstance interface
        return {
          id: data.catalogInstance?.id || `query-${catalogSlug}`, // Generate ID if not provided
          tenantId: data.id,
          subdomain: catalogSlug,
          isMainCatalog: false,
          name: data.companyName || `${catalogSlug} Catalog`,
          description: data.company?.description,
          status: 'active',
          branding: data.branding || {
            logo: data.logo,
            primaryColor: data.primaryColor || '#1976d2',
            secondaryColor: data.secondaryColor || '#dc004e',
            name: data.companyName,
            website: data.website,
          },
        };
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.warn(
          `[CATALOG-DETECTOR] Phase 5A: Catalog by slug API returned error ${response.status}:`,
          errorText,
        );

        // Don't retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          break;
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.warn(
          `[CATALOG-DETECTOR] Phase 5A: API call timed out after ${timeoutMs}ms (attempt ${attempt}/${maxRetries})`,
        );
      } else {
        console.error(
          `[CATALOG-DETECTOR] Phase 5A: API call failed (attempt ${attempt}/${maxRetries}):`,
          error,
        );
      }

      // If this was the last attempt, don't retry
      if (attempt === maxRetries) {
        break;
      }

      // Wait before retrying (exponential backoff)
      const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      console.log(`[CATALOG-DETECTOR] Phase 5A: Waiting ${delayMs}ms before retry...`);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  console.error('[CATALOG-DETECTOR] Phase 5A: All attempts to fetch catalog by slug failed');
  return null;
}
